import { catchError, map } from "rxjs/operators";
import { Injectable } from "@angular/core";

import { SocketService } from "../socket.service";
import { AssetWalletService } from "./asset_wallet.service";
import { MetalWallet } from "@models/asset/metal/metal_wallet";
import { Metal } from "@models/asset/metal/metal";
import { MetalService } from "../currency/metal.service";
import { MetalTransactionNotifyService } from "../notify/metal_transaction.notify.service";
import { Observable, of as observableOf } from "rxjs";
import { Address, ErrorClass } from "@models";
import { parseList } from "@helpers/functions";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { METAL_TRANSACTIONS_ROOM, METAL_WALLETS_ROOM } from "@helpers/constants";
import { MetalTransaction } from "@models/transaction";
import { GetPendingTradesGQLService } from "@gql/offer";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { DestructorService } from "@core-services/destructor.service";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { ValidationError } from "bp-common-models-lib";

const name = ServiceNames.METAL_WALLET;

@Injectable({
	providedIn: "root"
})
export class MetalWalletService extends AssetWalletService<MetalWallet, MetalTransaction, Metal> {
	public name: string = ServiceNames.METAL_WALLET;

	private canCreateMore: boolean = false;
	private cannotCreateReason: string = "";

	public TxType = MetalTransaction;
	public WalletType = MetalWallet;

	protected transactionSocketRoomName: string = METAL_TRANSACTIONS_ROOM;
	protected walletSocketRoomName: string = METAL_WALLETS_ROOM;

	public constructor(
		protected currencyService: MetalService,
		protected apicallService: ApicallService,
		protected infoService: InfoService,
		protected socketService: SocketService,
		protected translationService: TranslationService,
		protected initService: InitService,
		protected transactionNotifyService: MetalTransactionNotifyService,
		protected destructor: DestructorService,
		protected getPendingTradesGQLService: GetPendingTradesGQLService
	) {
		super(initService, destructor, name, getPendingTradesGQLService);
	}

	public async cancel(t: MetalTransaction): Promise<MetalTransaction> {
		const oldTransaction = new MetalTransaction();
		Object.assign(oldTransaction, t);
		const call = this.apicallService.deleteRequest(
			"wallet/" + t.getWalletId() + "/transaction/" + t.getId(),
			new ApicallOption(MetalTransaction)
		);
		return super.cancelSub(call, t, oldTransaction).toPromise();
	}

	public getParseData(wallets): MetalWallet[] {
		return parseList(wallets["attributes"]["commodity"]["metal"]["attributes"]["wallets"], MetalWallet);
	}

	public getAddressesForWallet(wallet): Observable<MetalWallet> {
		const option = new ApicallOption();
		option.raw = true;
		return this.apicallService.get(`wallet/${wallet.getId()}/addresses`, option).pipe(
			map((addresses) => {
				this.canCreateMore = addresses["can_create_more"];
				this.cannotCreateReason = addresses["cannot_create_reason"] || "";

				wallet.addresses = parseList(addresses["data"], Address);
				return wallet;
			}),
			catchError((error) => {
				if (error instanceof ErrorClass) {
					if (
						error instanceof ValidationError &&
						error.containsCode("multichain.coin_network_id.no_coin_network_for_given_asset")
					) {
						return observableOf(null);
					}

					this.infoService.setError(error);
					return observableOf(null);
				}
				console.error(error);
				return [];
			})
		);
	}
}
